import { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Grid,
  Typography,
  Modal,
  Box,
  Tooltip,
} from "@mui/material";
import { NextRouter, useRouter } from "next/router";
import { z } from "zod";
import {
  TTimeRanges,
  EXPLORER_TIME_RANGES,
  type DateRange,
  zCustomDateRange,
} from "#next/utils/shared";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import { selectSearchType } from "#next/redux/explorerSlice";
import { TimeRangeSelectorModalStyle } from "#next/components/modals/styles";
import { useExplorerSelector } from "#next/components/explorer/base/Hooks";

export const QueryStringTimeRanges = [
  "2d",
  "7d",
  "14d",
  "30d",
  "60d",
  "90d",
  "180d",
  "Custom",
] as const;

export const QueryStringToTimeRange = {
  [QueryStringTimeRanges[0]]: EXPLORER_TIME_RANGES[0],
  [QueryStringTimeRanges[1]]: EXPLORER_TIME_RANGES[1],
  [QueryStringTimeRanges[2]]: EXPLORER_TIME_RANGES[2],
  [QueryStringTimeRanges[3]]: EXPLORER_TIME_RANGES[3],
  [QueryStringTimeRanges[4]]: EXPLORER_TIME_RANGES[4],
  [QueryStringTimeRanges[5]]: EXPLORER_TIME_RANGES[5],
  [QueryStringTimeRanges[6]]: EXPLORER_TIME_RANGES[6],
  [QueryStringTimeRanges[7]]: EXPLORER_TIME_RANGES[7],
} as const;

const TimeRangeToQueryString = {
  [EXPLORER_TIME_RANGES[0]]: QueryStringTimeRanges[0],
  [EXPLORER_TIME_RANGES[1]]: QueryStringTimeRanges[1],
  [EXPLORER_TIME_RANGES[2]]: QueryStringTimeRanges[2],
  [EXPLORER_TIME_RANGES[3]]: QueryStringTimeRanges[3],
  [EXPLORER_TIME_RANGES[4]]: QueryStringTimeRanges[4],
  [EXPLORER_TIME_RANGES[5]]: QueryStringTimeRanges[5],
  [EXPLORER_TIME_RANGES[6]]: QueryStringTimeRanges[6],
  [EXPLORER_TIME_RANGES[7]]: QueryStringTimeRanges[7],
} as const;

const DefaultTimeRange: TTimeRanges = "Last 7 days";

/**
 * Get the current time range.
 *
 * Note: If there's no time range in the current query string
 * then this will add one, which will result in a reload. That's
 * correct because we never run a search without a time range, and
 * the query string is the canonical place for all filter settings
 * (of which time range is one)
 */
export const useTimeRange = (): {
  timeRange: TTimeRanges;
  customRange?: DateRange;
} => {
  const router = useRouter();

  const timerangeQueryString = z
    .enum(QueryStringTimeRanges)
    .safeParse(router.query[TimeRangeQueryStringKey]);

  if (timerangeQueryString.success) {
    if (timerangeQueryString.data === "Custom") {
      const customDateRange = zCustomDateRange.safeParse(
        router.query[TimeRangeCustomQueryStringKey],
      );
      if (customDateRange.success) {
        // Have to cast because of DateTime valid/invalid filtering that TS doesn't pick up.
        const customRange = [
          customDateRange.data[0] ?? null,
          customDateRange.data[1] ?? null,
        ] as DateRange;
        return { timeRange: timerangeQueryString.data, customRange };
      } else {
        return {
          timeRange: timerangeQueryString.data,
          customRange: [null, null],
        };
      }
    } else {
      return { timeRange: QueryStringToTimeRange[timerangeQueryString.data] };
    }
  }
  setTimeRange(router, DefaultTimeRange);
  return { timeRange: DefaultTimeRange };
};

const setTimeRange = (
  router: NextRouter,
  timeRange: TTimeRanges,
  customTimeRange?: DateRange,
) => {
  router.push(
    {
      pathname: router.pathname,
      query: {
        ...router.query,
        [TimeRangeQueryStringKey]: TimeRangeToQueryString[timeRange],
        [TimeRangeCustomQueryStringKey]: customTimeRange
          ? // Why not just string.join()? Because if you enter the end date first, it'll end up in the start date field.
            `${customTimeRange?.[0]?.toISODate() ?? ""},${customTimeRange?.[1]?.toISODate() ?? ""}`
          : undefined,
      },
    },
    undefined,
    { shallow: true },
  );
};

const TimeRangeQueryStringKey = "tr";
const TimeRangeCustomQueryStringKey = "trc";

export const TimeRangeSelector = () => {
  const router = useRouter();

  // Media only supports 30 days, and current setup doesn't support custom ranges for quick searches
  const searchType = useExplorerSelector(selectSearchType);
  const timeRangeSelectorIsDisabled = !!searchType;
  const nowDateTime = DateTime.utc().setZone("America/New_York").startOf("day");
  const timeRangeSelectorBackstop = nowDateTime.minus({ days: 179 });
  const timeRangeSelectorFrontstop = timeRangeSelectorBackstop.plus({
    days: 1,
  });

  const { timeRange, customRange } = useTimeRange();
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const enteringCustomRange =
    timeRange === "Custom" && (!customRange?.[0] || !customRange?.[1]);
  const [modalIsOpen, setModalIsOpen] = useState(enteringCustomRange);

  const handleClick = (timeRange: TTimeRanges, customTimeRange?: DateRange) => {
    setTimeRange(router, timeRange, customTimeRange);
    setModalIsOpen(false);
  };

  return (
    <>
      {!enteringCustomRange && (
        <FormControl
          size="small"
          sx={{
            minWidth: "10em",
            ml: 2,
            mr: 2,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography sx={{ marginRight: 1 }} variant="body2">
            View
          </Typography>
          <Tooltip
            title={
              timeRangeSelectorIsDisabled
                ? "Time ranges are not adjustable for searches."
                : null
            }
          >
            <Select
              labelId="time-range-label"
              variant="outlined"
              value={timeRange}
              onChange={(event) =>
                setTimeRange(router, event.target.value as TTimeRanges)
              }
              disabled={timeRangeSelectorIsDisabled}
            >
              {EXPLORER_TIME_RANGES.map((timeRange) => (
                <MenuItem key={timeRange} value={timeRange}>
                  {timeRange}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </FormControl>
      )}
      <Modal open={modalIsOpen}>
        <Box sx={TimeRangeSelectorModalStyle}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Grid container direction="column" m={0} justifyContent="center">
              <Grid item display="flex" justifyContent="center" mb={2}>
                <Typography>Please select a time range</Typography>
              </Grid>
              <Grid item mb={2}>
                <Stack direction="row">
                  <DatePicker
                    label="Start"
                    value={customRange?.[0]}
                    maxDate={
                      endDate
                        ? DateTime.min(nowDateTime, endDate.minus({ days: 1 }))
                        : nowDateTime
                    }
                    minDate={timeRangeSelectorBackstop}
                    onChange={setStartDate}
                    disableFuture
                  />
                  <DatePicker
                    label="End"
                    value={customRange?.[1]}
                    minDate={
                      startDate
                        ? DateTime.max(
                            timeRangeSelectorFrontstop,
                            startDate.plus({ days: 1 }),
                          )
                        : timeRangeSelectorFrontstop
                    }
                    onChange={setEndDate}
                    disableFuture
                  />
                </Stack>
              </Grid>
              <Grid item display="flex" justifyContent="space-around">
                <IconButton
                  onClick={() => handleClick("Custom", [startDate, endDate])}
                  disabled={
                    startDate == null ||
                    endDate == null ||
                    endDate <= startDate ||
                    startDate < timeRangeSelectorBackstop ||
                    endDate < timeRangeSelectorBackstop ||
                    startDate > nowDateTime ||
                    endDate > nowDateTime
                  }
                >
                  <DoneIcon />
                </IconButton>
                <IconButton onClick={() => handleClick(DefaultTimeRange)}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      </Modal>
    </>
  );
};
